<template>
  <div class="longtermorder">
    <section class="longtermorder__banner">
      <section class="longtermorder__banner-check" v-if="isChecking">
        <BaseTitle titleText="檢視長照單" />

        <StepProgressBar :steps="steps" :currentStep="order.orderStatus" />
        <div class="longtermorder__action-group">
          <b-button
            class="longtermorder__action-group-restore"
            variant="danger"
            @click="restoreOrderStatus"
            >恢復待驗單狀態</b-button
          >
          <BaseButtonGroup
            positiveText="編輯"
            negativeText="回上一頁"
            positiveType="button"
            negativeType="button"
            @positive="switch2Edit"
            @negative="$router.go(-1)"
            class="text-right"
          />
        </div>
      </section>
      <section class="longterorder__banner-edit" v-else>
        <BaseTitle titleText="編輯長照單" />
        <b-form class="longtermorder__form">
          <FormTitle titleText="特殊操作" />
          <div v-if="order.orderStatus === 5" class="text-danger">
            乘客取消原因：{{ order.cancelReason }}
          </div>
          <b-form-radio-group v-else class="longtermorder__form-radios" plain>
            <div>
              <b-form-radio
                class="mr-4"
                :value="0"
                @change="inputCancelReason($event, '乘客取消')"
                >乘客取消</b-form-radio
              >
              <b-form-radio
                class="mr-4"
                :value="1"
                @change="inputCancelReason($event, '乘客爽約')"
                >乘客爽約</b-form-radio
              >
              <b-form-radio :value="2" @change="inputCancelReason(2, '')"
                >其它
                <b-form-input
                  :disabled="cancelSelect !== 2"
                  :value="cancelReason"
                  @input="inputCancelReason(2, $event)"
                ></b-form-input>
              </b-form-radio>
            </div>
            <div>
              <b-button
                class="float-right"
                :disabled="!cancelReason"
                variant="danger"
                @click="cancelOne"
                >送出取消原因</b-button
              >
            </div>
          </b-form-radio-group>

          <div class="longtermorder__form-action-group">
            <b-button
              v-if="order.orderStatus === 1 || order.orderStatus === 2"
              class="py-2"
              variant="warning"
              @click="passengerOff"
            >
              乘客下車
            </b-button>
            <b-button class="py-2" variant="outline-danger" @click="deleteOne">
              封存長照單
            </b-button>
          </div>
        </b-form>
      </section>
    </section>

    <b-form class="longtermorder__form position-relative">
      <CancelIcon
        v-if="order.orderStatus === 5"
        class="longtermorder__form-status-icon"
      />
      <CheckIcon
        v-if="order.orderStatus === 4"
        class="longtermorder__form-status-icon"
      />
      <section class="longtermorder__form-passenger">
        <FormTitle titleText="乘客資訊" />

        <FormImmutableField
          title="乘客"
          :text="order.userInformation.name"
          class="longtermorder__form-field"
        />
        <FormImmutableField
          title="行政區"
          :text="order.userInformation.area"
          class="longtermorder__form-field"
        />
        <FormImmutableField
          title="住家地址"
          :text="order.userInformation.address"
          class="longtermorder__form-field"
        />
        <FormImmutableField
          title="聯絡人"
          :text="order.userInformation.contactPerson"
          class="longtermorder__form-field"
        />
        <FormImmutableField
          title="住家電話"
          :text="order.userInformation.telephoneNumber"
          class="longtermorder__form-field"
        />
        <FormImmutableField
          title="手機一"
          :text="order.userInformation.firstMobilePhoneNumber"
          class="longtermorder__form-field"
        />
        <FormImmutableField
          title="手機二"
          :text="order.userInformation.secondMobilePhoneNumber"
          class="longtermorder__form-field"
        />
      </section>

      <section
        class="longtermorder__form-immediate"
        v-if="order.immediateInformation"
      >
        <FormTitle titleText="即時資訊" />

        <FormImmutableField
          title="本月剩餘補助"
          :text="
            order.immediateInformation
              ? order.immediateInformation.subsidyLastAmount
              : ''
          "
          class="longtermorder__form-field"
        />
        <FormImmutableField
          title="社區式剩餘次數"
          :text="
            order.immediateInformation
              ? order.immediateInformation.lastCommunityShuttleFrequency
              : ''
          "
          class="longtermorder__form-field widerlabel"
        />
      </section>

      <section
        class="longtermorder__form-driver"
        v-if="Object.keys(order.driverInformation).length || !isChecking"
      >
        <FormTitle titleText="司機資訊" />

        <InputGroupDriver
          :driver="selectedDriver"
          :licenseNumber="order.licenseNumber ? order.licenseNumber : ''"
          :driverSearchResult="orderDriverIDSearchResult"
          :licenseNumberSearchResult="orderLicenseNumberSearchResult"
          :disabled="isChecking"
          @searchDriverId="searchDriverId"
          @selectDriverId="selectDriverId"
          @searchLicenseNumber="searchLicenseNumber"
          @selectLicenseNumber="selectLicenseNumber"
          class="longtermorder__form-mutable widerlabel small"
        />

        <section class="longtermorder__form-immutable">
          <FormImmutableField
            title="聯絡電話一"
            :text="order.driverInformation.firstContactNumber"
            class="longtermorder__form-field"
          />
          <FormImmutableField
            title="聯絡電話二"
            :text="order.driverInformation.secondContactNumber"
            class="longtermorder__form-field"
          />
          <FormImmutableField
            title="車機編號"
            :text="order.driverInformation.taximeterNumber"
            class="longtermorder__form-field"
          />
        </section>
      </section>

      <section>
        <FormTitle titleText="乘車資訊" />

        <section class="longtermorder__form-immutable">
          <BaseSelect
            labelText="*派單方式"
            :value="order.deliveryMethod"
            :text="deliveryMethodFormMap(order.deliveryMethod)"
            :options="deliveryMethodOptions"
            :disabled="isChecking"
            :required="true"
            @select="orderDataHandler($event, 'deliveryMethod')"
            class="longtermorder__form-field widerlabel"
          />

          <!-- <BaseDateTimePicker
            label="*預定上車時間"
            :date="toLocalDate(order.rideInformation.estimateGetOnTime)"
            :time="toLocalTime(order.rideInformation.estimateGetOnTime)"
            :disabled="isChecking"
            :required="true"
            @input="orderDataHandler($event, 'estimateGetOnTime')"
            class="longtermorder__form-field widerlabel"
          /> -->

          <BaseDateTimePicker
            label="*預定上車時間"
            :disabled="isChecking"
            :required="true"
            :value="toLocal(order.estimateGetOnTime)"
            @input="orderDataHandler($event, 'estimateGetOnTime')"
            class="longtermorder__form-field widerlabel"
          />

          <BaseDateTimePicker
            label="實際上車時間"
            :value="toLocal(order.getOnTime)"
            :disabled="isChecking"
            @input="orderDataHandler($event, 'getOnTime')"
            class="longtermorder__form-field widerlabel"
          />

          <BaseDateTimePicker
            label="實際下車時間"
            :value="toLocal(order.getOffTime)"
            :disabled="isChecking"
            @input="orderDataHandler($event, 'getOffTime')"
            class="longtermorder__form-field widerlabel"
          />

          <InputGroupLocation
            :onLocation="order.onLocation"
            :orderUserLocation="{ address: order.userInformation.address }"
            :onLocationGps="order.onLocationGps ? order.onLocationGps : []"
            :offLocation="order.offLocation"
            :offLocationGps="order.offLocationGps ? order.offLocationGps : []"
            :userHistoryAddressFields="userHistoryAddressFields"
            :userHistoryAddress="userHistoryAddress"
            :disabled="isChecking"
            @setOnLocation="orderDataHandler($event.value, $event.property)"
            @inputOnLocation="inputOnLocation"
            @setOnLocationGps="orderDataHandler($event.value, $event.property)"
            @setOffLocation="orderDataHandler($event.value, $event.property)"
            @inputOffLocation="inputOffLocation"
            @setOffLocationGps="orderDataHandler($event.value, $event.property)"
            class="small-padding"
          />
        </section>

        <section
          class="longtermorder__form-immutable longtermorder__form-GPS field-border-top field-border-bottom"
          v-if="isChecking"
        >
          <FormImmutableField
            title="實際上車GPS"
            :text="
              order.rideInformation.onLocationGps
                ? order.rideInformation.onLocationGps.join(', ')
                : ''
            "
            class="longtermorder__form-field"
          />

          <FormImmutableField
            title="實際下車GPS"
            :text="
              order.rideInformation.offLocationGps
                ? order.rideInformation.offLocationGps.join(', ')
                : ''
            "
            class="longtermorder__form-field"
          />
        </section>

        <section section class="longtermorder__form-mutable">
          <BaseInput
            :value="order.seat"
            :disabled="isChecking"
            name="seat"
            validation="required"
            type="number"
            labelText="*乘客數"
            @input="orderDataHandler(Number($event), 'seat')"
            class="longtermorder__form-field widerlabel"
          />

          <BaseCheckGroup
            labelText="偏好服務"
            :checked="order.service"
            :options="serviceOptions"
            :disabled="isChecking"
            @change="orderDataHandler($event, 'service')"
            class="longtermorder__form-field widerlabel"
          />

          <BaseTextarea
            labelText="客服備註"
            :value="order.serviceNote"
            :disabled="isChecking"
            @input="orderDataHandler($event, 'serviceNote')"
            class="longtermorder__form-field widerlabel"
          />
        </section>

        <section class="longtermorder__form-immutable">
          <FormImmutableField
            title="司機備註"
            :text="order.rideInformation.driverNote"
            class="longtermorder__form-field"
          />
        </section>
      </section>

      <section v-if="order.orderStatus" class="longtermorder__form-price">
        <FormTitle titleText="費用資訊" />

        <InputGroupFare
          :subsidyMethodOptions="subsidyMethodOptions"
          :subsidyMethod="order.subsidyMethod"
          :milage="order.milage"
          :metreFare="order.metreFare"
          :subsidyFare="order.subsidyFare"
          :actualFare="order.actualFare"
          :autoCountButtonShow="autoCountButtonShow"
          :disabled="isChecking"
          @selectSubsidyMethod="selectSubsidyMethod"
          @inputMilage="inputMilage"
          @inputMetreFare="inputMetreFare"
          @inputSubsidyFare="inputSubsidyFare"
          @inputActualFare="inputActualFare"
          @autoCountMetreFare="autoCountMetreFare"
          class="widerlabel small-padding"
        />

        <section class="longtermorder__form-immutable">
          <FormImmutableField
            title="區域類型"
            :text="areaTypeMap(order.fareInformation.areaType)"
            class="longtermorder__form-field"
          />
          <FormImmutableField
            title="身份別"
            :text="classMap(order.fareInformation.class)"
            class="longtermorder__form-field"
          />
          <FormImmutableField
            title="自付比例"
            :text="order.fareInformation.selfPayRatio"
            class="longtermorder__form-field"
          />
          <FormImmutableField
            title="驗證"
            :text="verificationMethodMap(order.verificationMethod)"
            class="longtermorder__form-field border-bottom-0"
          >
          </FormImmutableField>
          <div v-if="isChecking && order.verificationPhoto">
            <img
              :src="order.verificationPhoto"
              alt="乘客簽名"
              class="longtermorder__form-signature"
            />
          </div>
        </section>
      </section>

      <BaseButtonGroup
        v-if="!isChecking"
        positiveText="確認"
        negativeText="取消"
        @positive="onSubmit"
        @negative="isChecking = true"
        class="longtermorder__form-button-group text-right"
      />
    </b-form>
  </div>
</template>

<script>
import BaseTitle from '@/components/base/BaseTitle';
import BaseInput from '@/components/base/BaseInput';
import BaseSelect from '@/components/base/BaseSelect';
import BaseTextarea from '@/components/base/BaseTextarea';
import BaseButtonGroup from '@/components/base/BaseButtonGroup';
import BaseCheckGroup from '@/components/base/BaseCheckGroup';
import BaseDateTimePicker from '@/components/base/BaseDateTimePicker';
// import BaseDateTimePicker from '@/components/base/BaseDateTimepicker.vue';
import FormTitle from '@/components/commen/FormTitle';
import FormImmutableField from '@/components/commen/FormImmutableField';
import StepProgressBar from '@/components/commen/StepProgressBar';
import InputGroupDriver from '@/components/commen/InputGroupDriver';
import InputGroupLocation from '@/components/commen/InputGroupLocation';
import InputGroupFare from '@/components/longTerm/InputGroupFare';

import CancelIcon from '@/assets/images/close.svg';
import CheckIcon from '@/assets/images/check.svg';

import {
  deliveryMethodFormMap,
  subsidyMethodFormMap,
  classMap,
  areaTypeMap,
  verificationMethodMap
} from '@/utils/optionMaps';
import { mapGetters, mapActions } from 'vuex';
import { toLocal, timeFormat } from '@/utils/moment';
import { errorMessageParse } from '@/utils/errorMessage';

export default {
  name: 'LongTermOrder',
  components: {
    BaseTitle,
    BaseInput,
    BaseSelect,
    BaseTextarea,
    BaseButtonGroup,
    BaseCheckGroup,
    BaseDateTimePicker,
    FormTitle,
    FormImmutableField,
    StepProgressBar,
    InputGroupDriver,
    InputGroupLocation,
    InputGroupFare,
    CancelIcon,
    CheckIcon
  },
  data() {
    return {
      steps: [
        {
          step: 0,
          text: '找司機'
        },
        {
          step: 1,
          text: '待載客'
        },
        {
          step: 2,
          text: '載客中'
        },
        {
          step: 3,
          text: '待驗單'
        },
        {
          step: 4,
          text: '完成'
        }
      ],
      currentStep: 5,
      isChecking: true,
      selectedDriver: '',
      cancelSelect: null,
      cancelReason: ''
    };
  },
  methods: {
    ...mapActions({
      updateOrder: 'longTerm/updateOrder',
      fetchOrder: 'longTerm/fetchOrder',
      fetchServiceOptions: 'longTerm/fetchServiceOptions',
      deleteOrder: 'longTerm/deleteOrder',
      cancelOrder: 'longTerm/cancelOrder',
      updateOrderStatus: 'longTerm/updateOrderStatus',
      fetchOrderDriverSearch: 'longTerm/fetchOrderDriverSearch',
      fetchOrderAutoCountMetreFare: 'longTerm/fetchOrderAutoCountMetreFare'
    }),
    cancelEdit() {
      this.isChecking = true;
    },
    inputCancelReason(value, reason) {
      this.cancelSelect = value;
      this.cancelReason = reason;
    },
    async switch2Edit() {
      this.isChecking = false;
      const userId = this.order.userInformation.longTermCareUserId;
      await this.$store.dispatch('longTerm/fetchUserDetailSearch', userId);
    },
    orderDataHandler(value, property) {
      console.log(value, property);
      const data = this.orderData;
      data[property] = value;
      this.$store.commit('longTerm/orderData', data);
    },
    searchDriverId(value) {
      this.fetchOrderDriverSearch({
        dataType: 'driverId',
        driverData: {
          call_number: value
        }
      });
    },
    searchLicenseNumber(value) {
      this.fetchOrderDriverSearch({
        dataType: 'licenseNumber',
        driverData: {
          license_number: value
        }
      });
    },
    selectDriverId(item) {
      this.selectedDriver = item.value;

      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        driverId: item.id,
        licenseNumber: item.licenseNumber
      });
    },
    selectLicenseNumber(item) {
      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        licenseNumber: item.licenseNumber
      });
    },
    inputOnLocation(location) {
      const onLocationGps =
        location.longitude && location.latitude
          ? [location.longitude, location.latitude]
          : [];
      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        onLocation: location.address,
        onLocationGps: onLocationGps
      });
    },
    inputOffLocation(location) {
      const offLocationGps =
        location.longitude && location.latitude
          ? [location.longitude, location.latitude]
          : [];
      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        offLocation: location.address,
        offLocationGps: offLocationGps
      });
    },
    selectSubsidyMethod(value) {
      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        subsidyMethod: value
      });
    },
    inputMilage(value) {
      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        milage: value
      });
    },
    inputMetreFare(value) {
      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        metreFare: value
      });
    },
    inputSubsidyFare(value) {
      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        subsidyFare: value
      });
    },
    inputActualFare(value) {
      this.$store.commit('longTerm/orderData', {
        ...this.orderData,
        actualFare: value
      });
    },
    async autoCountMetreFare() {
      try {
        await this.fetchOrderAutoCountMetreFare({
          longTermCareUserId: this.orderData.longTermCareUserId,
          fareData: {
            subsidy_method: this.orderData.subsidyMethod,
            metre_fare: this.orderData.metreFare,
            time: this.orderData.estimateGetOnTime
          }
        });
      } catch (err) {
        console.warn(err);
        this.$store.commit('errorMessage', errorMessageParse(err));
        this.$root.$emit('bv::show::modal', 'error-modal');
      }
    },
    deliveryMethodFormMap(value) {
      return deliveryMethodFormMap(value);
    },
    subsidyMethodFormMap(value) {
      return subsidyMethodFormMap(value);
    },
    classMap(value) {
      return classMap(value);
    },
    areaTypeMap(value) {
      return areaTypeMap(value);
    },
    verificationMethodMap(value) {
      return verificationMethodMap(value);
    },
    toLocal(datetime) {
      console.log(datetime);
      if (!datetime) return;
      return toLocal(datetime);
    },
    timeFormat(datetime) {
      return timeFormat(datetime);
    },
    orderDataProcess(data) {
      return Object.keys(data).reduce((accu, curr) => {
        if (curr === 'driverId' && !data[curr]) return accu;
        if (curr === 'licenseNumber' && !data[curr]) return accu;
        if (curr === 'milage' && !data[curr]) return accu;
        if (curr === 'getOnTime' && !data[curr]) return accu;
        if (curr === 'getOffTime' && !data[curr]) return accu;

        if (curr === 'subsidyFare' && !data[curr]) return accu;
        if (
          curr === 'subsidyFare' &&
          data[curr] === this.order.fareInformation[curr]
        )
          return accu;
        if (curr === 'metreFare' && !data[curr]) return accu;
        if (
          curr === 'metreFare' &&
          data[curr] === this.order.fareInformation[curr]
        )
          return accu;

        if (curr === 'actualFare' && !data[curr]) return accu;
        if (
          curr === 'actualFare' &&
          data[curr] === this.order.fareInformation[curr]
        )
          return accu;
        accu[curr] = data[curr];
        return accu;
      }, {});
    },
    async onSubmit() {
      const orderData = this.orderDataProcess(this.orderData);
      console.log(orderData);
      try {
        await this.updateOrder({
          orderId: this.$route.params.id,
          orderData
        });
        this.$bvToast.toast('長照單更新成功', {
          toaster: 'b-toaster-top-center',
          variant: 'success',
          solid: true
        });
        this.$router.go(-1);
      } catch (err) {
        console.warn(err);
        this.$store.commit('errorMessage', errorMessageParse(err));
        this.$root.$emit('bv::show::modal', 'error-modal');
      }
    },
    async passengerOff() {
      try {
        await this.updateOrderStatus({
          orderId: this.$route.params.id,
          payload: {
            status: 3
          }
        });
        this.$bvToast.toast('長照單狀態更新成功', {
          toaster: 'b-toaster-top-center',
          variant: 'success',
          solid: true
        });
        await this.fetchOrder(this.$route.params.id);
        this.isChecking = true;
      } catch (err) {
        console.warn(err);
        this.$store.commit('errorMessage', errorMessageParse(err));
        this.$root.$emit('bv::show::modal', 'error-modal');
      }
    },
    async deleteOne() {
      try {
        await this.deleteOrder(this.$route.params.id);
        this.$bvToast.toast('封存長照單成功', {
          toaster: 'b-toaster-top-center',
          variant: 'success',
          solid: true
        });
        this.$router.push({ name: 'LongTerm' });
      } catch (err) {
        console.warn(err);
        this.$store.commit('errorMessage', errorMessageParse(err));
        this.$root.$emit('bv::show::modal', 'error-modal');
      }
    },
    async cancelOne() {
      try {
        await this.cancelOrder({
          orderId: this.$route.params.id,
          cancelReason: {
            cancelReason: this.cancelReason
          }
        });
        this.$bvToast.toast('取消長照單成功', {
          toaster: 'b-toaster-top-center',
          variant: 'success',
          solid: true
        });
        await this.fetchOrder(this.$route.params.id);
        this.isChecking = true;
      } catch (err) {
        console.warn(err);
        this.$store.commit('errorMessage', errorMessageParse(err));
        this.$root.$emit('bv::show::modal', 'error-modal');
      }
    },
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref);
      }
      return null;
    },
    async restoreOrderStatus() {
      try {
        await this.updateOrderStatus({
          orderId: this.$route.params.id,
          payload: {
            status: 3
          }
        });
        await this.fetchOrder(this.$route.params.id);
        await this.fetchServiceOptions();
      } catch (err) {
        console.warn(err);
      }
    }
  },
  computed: {
    ...mapGetters({
      deliveryMethodOptions: 'longTerm/deliveryMethodOptions',
      subsidyMethodOptions: 'longTerm/subsidyMethodOptions',
      serviceOptions: 'longTerm/serviceOptions',
      userHistoryAddressFields: 'longTerm/userHistoryAddressFields',
      order: 'longTerm/order',
      orderData: 'longTerm/orderData',
      orderDriverIDSearchResult: 'longTerm/orderDriverIDSearchResult',
      orderLicenseNumberSearchResult: 'longTerm/orderLicenseNumberSearchResult',
      userHistoryAddress: 'longTerm/userHistoryAddress'
    }),
    autoCountButtonShow() {
      const {
        subsidyMethod,
        longTermCareUserId,
        metreFare,
        estimateGetOnTime
      } = this.order;
      return !(
        longTermCareUserId &&
        subsidyMethod !== null &&
        metreFare &&
        estimateGetOnTime
      );
    }
  },
  created() {
    if (this.$route.params.action === 'edit') this.isChecking = false;
  },
  mounted() {
    if (!this.order.driverInformation.name) {
      this.selectedDriver = '';
      return;
    }
    const { callNumber, name } = this.order.driverInformation;
    this.selectedDriver = `${callNumber}-${name}`;
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('longTerm/orderData', {
      longTermCareUserId: null,
      deliveryMethod: null,
      estimateGetOnTime: '',
      getOnTime: '',
      getOffTime: '',
      onLocation: '',
      onLocationGps: [],
      offLocation: '',
      offLocationGps: [],
      seat: 2,
      service: [],
      serviceNote: '',
      driverId: '',
      licenseNumber: '',
      subsidyMethod: null,
      milage: 0,
      metreFare: 0,
      subsidyFare: 0,
      actualFare: 0
    });
    this.$store.commit('longTerm/userDetailSearchResult', {
      name: '',
      address: '',
      service: [],
      payForOwnselfProportion: null,
      selfPayRatio: null,
      subsidyLastAmount: null,
      lastCommunityShuttleFrequency: null,
      verification: false,
      historyAddressData: []
    });
    next();
  }
};
</script>

<style lang="scss" scoped>
.longtermorder {
  width: 65%;
  max-width: 950px;
  margin: auto;

  &__form {
    padding: 20px 20px;
    margin-top: 20px;
    border: 2px solid #ebeef5;

    &-status-icon {
      position: absolute;
      width: 120px;
      height: 120px;
      top: 100px;
      right: 30px;
      opacity: 0.5;
      z-index: 2;
    }

    &-radios {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #ededed;
      padding-bottom: 30px;
      margin-top: 30px;
      input {
        display: inline-block;
        width: 200px;
        margin-left: 10px;
      }
      button {
        font-size: 13px;
      }
    }

    &-action-group {
      display: flex;
      margin-top: 30px;
      button {
        flex: 1 1 50%;
        &:first-child {
          margin-right: 10px;
        }
      }
    }

    &-mutable {
      border-bottom: 1px solid #ededed;
      &:last-child {
        border-bottom: none;
      }
    }

    &-field {
      padding: 10px 80px;
      border-bottom: 1px solid #ededed;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: #fafafa;
      }

      &-sub {
        display: flex;
        align-items: center;
        &:first-child {
          margin-right: 80px;
        }
      }

      &-location-button-group {
        display: flex;
        flex: 0 0 180px;
        margin-left: 10px;
        button {
          &:first-child {
            margin-right: 10px;
          }
        }
      }

      &-price {
        button {
          flex: 0 0 100px;
          margin-left: 10px;
        }
      }
    }

    &-ride-coordinate {
      padding: 0 80px;
      border-top: 1px solid #ededed;
      border-bottom: 1px solid #ededed;
      .table {
        color: #73879c;
      }
    }

    &-GPS {
      line-height: 1.5rem;
    }

    &-button-group {
      margin-top: 50px;
    }

    &-signature {
      margin-left: 180px;
      width: 500px;
    }
  }

  &__action-group {
    display: flex;
    justify-content: flex-end;
    &-restore {
      margin-right: 30px;
    }
  }

  .custom-select,
  .form-control {
    font-size: 13px;
  }

  .form-control::placeholder {
    color: #c0c4cc;
    font-size: 13px;
  }

  ::v-deep .table thead th {
    border-bottom: 1px solid #dee2e6;
    border-top: none;
  }

  .btn-outline-secondary {
    background-color: #fff;
    border: 1px solid #dcdfe6;
    color: #606266;
    font-size: 13px;
    &:hover {
      background-color: #606266;
      color: #fff;
    }
  }
}

.field-border-top {
  border-top: 1px solid #ededed;
}

.field-border-bottom {
  border-bottom: 1px solid #ededed;
}
</style>
